import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Divider,
    IconButton,
    InputAdornment,
    Link,
    Paper,
    Stack,
    TextField,
    Typography,
    useTheme,
    useMediaQuery
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { AnimatedPasswordCheck } from "../components/PasswordCheck.tsx";
import { isStrongPassword } from "../../../src/shared";
import { fetchWithError } from "../utils/fetchWithError.ts";
import { useSnackbar } from 'notistack';
import tintoaLogoWhite from "../img/logo_tintoa_white.png";
import tintoaLogoBlack from "../img/logo_tintoa_black.png";
import { TermsAndConditions } from "../components/TermsAndConditions.tsx";
import { AuthPageShell } from "../components/auth/AuthPageShell.tsx";

export const SignupScreen = () => {
    const [formData, setFormData] = useState({
        email: "",
        password: "",
        confirmPassword: "",
        firstname: "",
        lastname: ""
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordHint, setShowPasswordHint] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [showAcceptTermsError, setShowAcceptTermsError] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const isDarkTheme = theme.palette.mode === 'dark';
    const { enqueueSnackbar } = useSnackbar();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

    // Store clientId in cookie when signup page is loaded with clientId parameter
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const clientId = params.get("clientId");
        if (clientId) {
            // Store clientId in cookie for OAuth flow
            document.cookie = `oauth_client_id=${clientId}; path=/; max-age=300; SameSite=Lax`;
        }
    }, []);

    useEffect(() => {
        if (acceptTerms) {
            document.cookie =
                "oauth_terms_accepted=1; path=/; max-age=300; SameSite=Lax";
            return;
        }
        document.cookie =
            "oauth_terms_accepted=; path=/; max-age=0; SameSite=Lax";
    }, [acceptTerms]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (formData.password !== formData.confirmPassword) {
            enqueueSnackbar("Die Passwörter stimmen nicht überein.", { variant: 'error' });
            return;
        }
        if (!acceptTerms) {
            setShowAcceptTermsError(true);
            enqueueSnackbar(
                "Bitte akzeptiere Nutzungsbedingungen und Datenschutzerklärung.",
                { variant: "error" }
            );
            return;
        }
        setShowAcceptTermsError(false);

        setIsLoading(true);

        try {
            const baseUrl = "/auth/signup";
            const url = new URL(baseUrl, window.location.origin);

            await fetchWithError(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email: formData.email,
                    password: formData.password,
                    firstname: formData.firstname,
                    lastname: formData.lastname,
                    acceptTerms: true
                }),
                credentials: "include",
            });
            enqueueSnackbar("Prüfe deinen E-Mail Posteingang um deine Registrierung abzuschließen", { variant: 'success' });
            navigate("/app/login");
        } catch (error) {
            // Display the specific error message from the backend
            if (error instanceof Error) {
                enqueueSnackbar(error.message, { variant: 'error' });
            } else {
                enqueueSnackbar("Registrierung fehlgeschlagen. Bitte überprüfe deine Eingaben.", { variant: 'error' });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthPageShell
            isDarkTheme={isDarkTheme}
            desktopSidePanel={
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        color: isDarkTheme ? "white" : "text.primary",
                        textAlign: "center",
                        px: 8,
                        py: 4,
                        transition: "all 0.4s ease-in-out"
                    }}
                >
                    <img
                        src={isDarkTheme ? tintoaLogoWhite : tintoaLogoBlack}
                        alt="tintoa-logo"
                        style={{
                            height: 48,
                            marginBottom: 32
                        }}
                    />
                    <Typography
                        variant="h2"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            mb: 2
                        }}
                    >
                        Konto erstellen
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{
                            opacity: 0.9,
                            maxWidth: 400,
                            lineHeight: 1.6
                        }}
                    >
                        Registriere dich für ein neues Konto und starte deine Reise mit uns.
                    </Typography>
                </Box>
            }
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: { xs: 600, lg: "100%" },
                    mx: "auto",
                    display: "flex",
                    flexDirection: "column",
                    flex: { lg: 1 },
                    minHeight: { lg: 0 },
                    height: { lg: "100%" },
                    transition: "all 0.3s ease-in-out"
                }}
            >
                    <Paper
                        elevation={0}
                        sx={{
                            height: isLargeScreen ? '100%' : 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            p: { xs: 3, sm: 4, lg: 5 },
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: isLargeScreen ? 0 : 2,
                            bgcolor: theme.palette.authCard!,
                            backdropFilter: 'blur(25px)',
                            boxShadow: 'none',
                            transition: 'all 0.3s ease-in-out'
                        }}
                    >
                        <Stack 
                            spacing={4} 
                            sx={{ 
                                flex: isLargeScreen ? 1 : 'none',
                                justifyContent: isLargeScreen ? 'center' : 'flex-start'
                            }}
                        >
                            {/* Logo and title for small screens */}
                            {!isLargeScreen && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <img
                                        src={theme.palette.mode === 'dark' ? tintoaLogoWhite : tintoaLogoBlack}
                                        alt="tintoa-logo"
                                        style={{ 
                                            height: 32,
                                            marginBottom: 24
                                        }}
                                    />
                                    <Typography 
                                        variant="h4" 
                                        component="h1" 
                                        gutterBottom
                                        sx={{ color: 'text.primary' }}
                                    >
                                        Konto erstellen
                                    </Typography>
                                    <Typography 
                                        sx={{ 
                                            color: isDarkTheme ? 'text.secondary' : 'text.primary',
                                            opacity: isDarkTheme ? 1 : 0.7
                                        }}
                                    >
                                        Registriere dich für ein neues Konto
                                    </Typography>
                                </Box>
                            )}

                            {/* Title for large screens */}
                            {isLargeScreen && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography 
                                        variant="h4" 
                                        component="h1" 
                                        gutterBottom
                                        sx={{ color: 'text.primary' }}
                                    >
                                        Registrierung
                                    </Typography>
                                    <Typography 
                                        sx={{ 
                                            color: isDarkTheme ? 'text.secondary' : 'text.primary',
                                            opacity: isDarkTheme ? 1 : 0.7
                                        }}
                                    >
                                        Fülle die Felder aus, um dein Konto zu erstellen
                                    </Typography>
                                </Box>
                            )}

                            <form onSubmit={handleSubmit}>
                                <Stack spacing={3}>
                                    <Stack 
                                        direction={{ xs: 'column', sm: 'row' }} 
                                        spacing={2}
                                    >
                                        <TextField
                                            label="Vorname"
                                            name="firstname"
                                            value={formData.firstname}
                                            onChange={handleChange}
                                            required
                                            fullWidth
                                            size={isLargeScreen ? "medium" : "small"}
                                        />
                                        <TextField
                                            label="Nachname"
                                            name="lastname"
                                            value={formData.lastname}
                                            onChange={handleChange}
                                            required
                                            fullWidth
                                            size={isLargeScreen ? "medium" : "small"}
                                        />
                                    </Stack>
                                    <TextField
                                        label="E-Mail"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        fullWidth
                                        autoComplete="email"
                                        size={isLargeScreen ? "medium" : "small"}
                                    />
                                    <TextField
                                        label="Passwort"
                                        name="password"
                                        type={showPassword ? "text" : "password"}
                                        value={formData.password}
                                        onChange={(e) => {
                                            handleChange(e);
                                            if (!showPasswordHint) {
                                                setShowPasswordHint(true);
                                            }
                                        }}
                                        required
                                        fullWidth
                                        autoComplete="new-password"
                                        size={isLargeScreen ? "medium" : "small"}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                        size={isLargeScreen ? "medium" : "small"}
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <AnimatedPasswordCheck
                                        show={showPasswordHint && !isStrongPassword(formData.password).isStrongPassword}
                                        password={formData.password}
                                    />
                                    <TextField
                                        label="Passwort bestätigen"
                                        name="confirmPassword"
                                        type={showPassword ? "text" : "password"}
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        required
                                        fullWidth
                                        autoComplete="new-password"
                                        size={isLargeScreen ? "medium" : "small"}
                                        error={formData.password !== formData.confirmPassword && formData.confirmPassword !== ""}
                                        helperText={
                                            formData.password !== formData.confirmPassword && formData.confirmPassword !== ""
                                                ? "Die Passwörter stimmen nicht überein"
                                                : ""
                                        }
                                    />
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size={isLargeScreen ? "large" : "medium"}
                                        disabled={isLoading || !acceptTerms}
                                        fullWidth
                                        sx={{
                                            py: isLargeScreen ? 1.5 : 1,
                                            fontSize: isLargeScreen ? '1.1rem' : '1rem'
                                        }}
                                    >
                                        {isLoading ? "Wird registriert..." : "Registrieren"}
                                    </Button>
                                </Stack>
                            </form>

                            <Divider sx={{ my: 2 }}>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: isDarkTheme ? 'text.secondary' : 'text.primary',
                                        opacity: 0.6,
                                        px: 2
                                    }}
                                >
                                    oder
                                </Typography>
                            </Divider>

                            <Button
                                variant="outlined"
                                size={isLargeScreen ? "large" : "medium"}
                                fullWidth
                                disabled={isLoading || !acceptTerms}
                                onClick={() => {
                                    if (!acceptTerms) {
                                        setShowAcceptTermsError(true);
                                        enqueueSnackbar(
                                            "Bitte akzeptiere Nutzungsbedingungen und Datenschutzerklärung.",
                                            { variant: "error" }
                                        );
                                        return;
                                    }
                                    // Mark intent as signup so backend may create a new account
                                    document.cookie = 'oauth_intent=signup; path=/; max-age=300; SameSite=Lax';
                                    // clientId is already stored in cookie from useEffect
                                    window.location.href = '/auth/google';
                                }}
                                sx={{
                                    py: isLargeScreen ? 1.5 : 1,
                                    fontSize: isLargeScreen ? '1.1rem' : '1rem',
                                    borderColor: theme.palette.input!.border,
                                    color: 'text.primary',
                                    '&:hover': {
                                        borderColor: theme.palette.input!.borderFocused,
                                        backgroundColor: theme.palette.input!.background
                                    }
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        mr: 1
                                    }}
                                >
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 18 18"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g fill="none" fillRule="evenodd">
                                            <path
                                                d="M17.64 9.2045c0-.6371-.0573-1.2516-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7955 2.7164v2.2581h2.9087c1.7023-1.5668 2.6836-3.8741 2.6836-6.6146z"
                                                fill={theme.palette.semantic!.brand.google.blue}
                                            />
                                            <path
                                                d="M9 18c2.4295 0 4.4673-.7955 5.9564-2.1805l-2.9087-2.2581c-.8046.54-1.8364.8591-3.0477.8591-2.3441 0-4.3273-1.5836-5.0382-3.7105H.9573v2.3318C2.4382 15.9836 5.4818 18 9 18z"
                                                fill={theme.palette.semantic!.brand.google.green}
                                            />
                                            <path
                                                d="M3.9614 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.9582H.9573A8.9965 8.9965 0 0 0 0 9c0 1.4523.3477 2.8268.9573 4.0418l3.0041-2.3318z"
                                                fill={theme.palette.semantic!.brand.google.yellow}
                                            />
                                            <path
                                                d="M9 3.5795c1.3214 0 2.5082.4545 3.4409 1.3455l2.5818-2.5818C12.4636.8918 10.4255 0 9 0 5.4818 0 2.4382 2.0164.9573 4.9582L3.9614 7.29C4.6723 5.1636 6.6555 3.5795 9 3.5795z"
                                                fill={theme.palette.semantic!.brand.google.red}
                                            />
                                        </g>
                                    </svg>
                                </Box>
                                Weiter mit Google
                            </Button>

                            <Divider sx={{ my: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: isDarkTheme ? "text.secondary" : "text.primary",
                                        opacity: 0.7,
                                        px: 2
                                    }}
                                >
                                    Nutzungsbedingungen & Datenschutz
                                </Typography>
                            </Divider>

                            <TermsAndConditions
                                method="signup"
                                acceptTerms={acceptTerms}
                                onAcceptTermsChange={(checked) => {
                                    setAcceptTerms(checked);
                                    if (checked) {
                                        setShowAcceptTermsError(false);
                                    }
                                }}
                                showAcceptTermsError={showAcceptTermsError}
                            />

                            <Box sx={{ textAlign: 'center' }}>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: isDarkTheme ? 'text.secondary' : 'text.primary',
                                        opacity: isDarkTheme ? 1 : 0.7
                                    }}
                                >
                                    Bereits ein Konto?{" "}
                                    <Link 
                                        component="button"
                                        onClick={() => navigate('/app/login')}
                                        underline="hover"
                                        sx={{ 
                                            color: 'primary.main',
                                            fontWeight: 500,
                                            border: 'none',
                                            background: 'none',
                                            p: 0,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Jetzt anmelden
                                    </Link>
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Box>
        </AuthPageShell>
    );
};
